import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';

// Start mounting a section once it is within one viewport height of the screen.
const NEAR_VIEWPORT_MARGIN = '100% 0px';
// Upper bound on how long the browser may stay busy before an idle mount.
const IDLE_TIMEOUT_MS = 2000;

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

// Runs the callback once the page has loaded and the main thread is idle.
function whenIdleAfterLoad(callback: () => void): () => void {
  const idleWindow = window as IdleWindow;
  let idleId: number | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = () => {
    if (idleWindow.requestIdleCallback) {
      idleId = idleWindow.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    } else {
      timer = setTimeout(callback, IDLE_TIMEOUT_MS);
    }
  };

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });

  return () => {
    window.removeEventListener('load', schedule);
    if (idleId !== undefined) idleWindow.cancelIdleCallback?.(idleId);
    if (timer !== undefined) clearTimeout(timer);
  };
}

interface DeferredSectionProps {
  id: string;
  // Tailwind min-height classes close to the section's rendered height, so
  // the placeholder holds its space and nothing below it moves on mount.
  placeholderClassName: string;
  // Mount straight away, e.g. when the URL targets a section by hash.
  eager?: boolean;
  children: ReactNode;
}

// Below-the-fold landing sections (and the chunks they pull in, three.js
// among them) stay off the critical path: each mounts when it comes near the
// viewport or once the page is idle after load, whichever happens first.
// The pending placeholder carries data-deferred-pending so
// scripts/prerender.mjs can wait until every section has rendered.
export default function DeferredSection({ id, placeholderClassName, eager = false, children }: DeferredSectionProps) {
  const ref = useRef<HTMLElement>(null);
  const [isMounted, setIsMounted] = useState(eager);

  useEffect(() => {
    const node = ref.current;
    if (isMounted || !node) return;

    const mount = () => setIsMounted(true);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) mount();
      },
      { rootMargin: NEAR_VIEWPORT_MARGIN },
    );
    observer.observe(node);
    const cancelIdle = whenIdleAfterLoad(mount);

    return () => {
      observer.disconnect();
      cancelIdle();
    };
  }, [isMounted]);

  const placeholder = <div aria-hidden="true" className={placeholderClassName} />;

  return (
    <section id={id} ref={ref}>
      {isMounted ? (
        <Suspense fallback={placeholder}>{children}</Suspense>
      ) : (
        <div data-deferred-pending="" aria-hidden="true" className={placeholderClassName} />
      )}
    </section>
  );
}
